import { useMemo } from 'react'
import { LineChart, Line, ResponsiveContainer } from 'recharts'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import type { MetricSnapshot, MetricSummary } from '../../types'

interface MetricKPICardProps {
  title: string
  metricName: string
  summary: MetricSummary | undefined
  timeSeries: MetricSnapshot[]
  format?: 'number' | 'percent' | 'bytes' | 'ms' | 'duration'
  icon?: React.ReactNode
  color?: 'blue' | 'green' | 'red' | 'amber' | 'purple'
  invertTrend?: boolean
}

function formatValue(value: number | null, format: string): string {
  if (value === null || value === undefined) return '-'

  switch (format) {
    case 'percent':
      return `${value.toFixed(1)}%`
    case 'bytes':
      if (value >= 1e9) return `${(value / 1e9).toFixed(1)}GB`
      if (value >= 1e6) return `${(value / 1e6).toFixed(1)}MB`
      if (value >= 1e3) return `${(value / 1e3).toFixed(1)}KB`
      return `${value}B`
    case 'ms':
    case 'duration':
      if (value >= 1000) return `${(value / 1000).toFixed(2)}s`
      return `${value.toFixed(0)}ms`
    default:
      if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`
      if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`
      return value.toLocaleString(undefined, { maximumFractionDigits: 1 })
  }
}

const colorClasses = {
  blue: { bg: 'bg-blue-50', text: 'text-blue-600', line: '#3b82f6' },
  green: { bg: 'bg-green-50', text: 'text-green-600', line: '#22c55e' },
  red: { bg: 'bg-red-50', text: 'text-red-600', line: '#ef4444' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-600', line: '#f59e0b' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600', line: '#8b5cf6' },
}

export function MetricKPICard({
  title,
  metricName,
  summary,
  timeSeries,
  format = 'number',
  icon,
  color = 'blue',
  invertTrend = false,
}: MetricKPICardProps) {
  // Extract sparkline data for this metric
  const sparklineData = useMemo(() => {
    return timeSeries
      .map(snapshot => ({
        value: snapshot.metrics[metricName] ?? null,
        timestamp: snapshot.created_at,
      }))
      .filter(d => d.value !== null)
      .slice(-20) // Last 20 data points for sparkline
  }, [timeSeries, metricName])

  // Calculate trend (compare recent half to earlier half)
  const trend = useMemo(() => {
    if (sparklineData.length < 4) return { direction: 'flat' as const, percent: 0 }

    const midpoint = Math.floor(sparklineData.length / 2)
    const earlier = sparklineData.slice(0, midpoint)
    const recent = sparklineData.slice(midpoint)

    const earlierAvg = earlier.reduce((sum, d) => sum + (d.value ?? 0), 0) / earlier.length
    const recentAvg = recent.reduce((sum, d) => sum + (d.value ?? 0), 0) / recent.length

    if (earlierAvg === 0) return { direction: 'flat' as const, percent: 0 }

    const percentChange = ((recentAvg - earlierAvg) / earlierAvg) * 100

    if (Math.abs(percentChange) < 2) return { direction: 'flat' as const, percent: 0 }
    return {
      direction: percentChange > 0 ? ('up' as const) : ('down' as const),
      percent: Math.abs(percentChange),
    }
  }, [sparklineData])

  const colors = colorClasses[color]
  const latestValue = summary?.latest

  // Determine if trend is good or bad
  const isPositive = invertTrend ? trend.direction === 'down' : trend.direction === 'up'
  const trendColor =
    trend.direction === 'flat' ? 'text-slate-400' : isPositive ? 'text-green-500' : 'text-red-500'

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        {icon && (
          <div className={`w-6 h-6 rounded-lg ${colors.bg} flex items-center justify-center`}>
            <span className={`${colors.text} w-3.5 h-3.5`}>{icon}</span>
          </div>
        )}
        <span className="text-sm text-slate-500 truncate">{title}</span>
      </div>

      {/* Value + Trend */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="text-2xl font-bold text-slate-900">
          {formatValue(latestValue ?? null, format)}
        </span>
        <div className={`flex items-center gap-1 text-sm ${trendColor}`}>
          {trend.direction === 'up' && <TrendingUp className="w-4 h-4" />}
          {trend.direction === 'down' && <TrendingDown className="w-4 h-4" />}
          {trend.direction === 'flat' && <Minus className="w-4 h-4" />}
          {trend.percent > 0 && <span className="text-xs">{trend.percent.toFixed(1)}%</span>}
        </div>
      </div>

      {/* Sparkline */}
      {sparklineData.length > 1 && (
        <div style={{ width: '100%', height: 40 }}>
          <ResponsiveContainer width="100%" height={40}>
            <LineChart data={sparklineData}>
              <Line
                type="monotone"
                dataKey="value"
                stroke={colors.line}
                strokeWidth={1.5}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Min/Max footer */}
      {summary && (summary.min !== null || summary.max !== null) && (
        <div className="flex justify-between text-xs text-slate-400 mt-2 pt-2 border-t border-slate-100">
          <span>Min: {formatValue(summary.min, format)}</span>
          <span>Max: {formatValue(summary.max, format)}</span>
        </div>
      )}
    </div>
  )
}
